import { getIn, setIn } from 'formik';
import deepEqual from 'fast-deep-equal';

export interface MergeRemoteValuesResult {
  readonly merged: Record<any, any>;
  readonly changedPaths: string[];
}

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const leafPaths = (record: Record<string, any>, prefix = ''): string[] =>
  Object.keys(record).reduce<string[]>((paths, key) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = record[key];

    return isPlainObject(value) ? [...paths, ...leafPaths(value, path)] : [...paths, path];
  }, []);

export const mergeRemoteValues = (
  prev: Record<any, any>,
  next: Record<any, any>,
  values: Record<any, any>,
): MergeRemoteValuesResult => {
  const changedPaths: string[] = [];
  let merged = values;

  leafPaths(next).forEach(path => {
    const prevValue = getIn(prev, path);
    const nextValue = getIn(next, path);
    const formValue = getIn(values, path);

    // Server unchanged, or the user has already diverged from what the server last said (user wins).
    if (deepEqual(nextValue, prevValue) || !deepEqual(formValue, prevValue)) {
      return;
    }

    const isArrayField = Array.isArray(formValue) || Array.isArray(prevValue);
    const resolvedValue = isArrayField && !Array.isArray(nextValue) ? [] : nextValue;

    merged = setIn(merged, path, resolvedValue);
    changedPaths.push(path);
  });

  return { merged, changedPaths };
};
